import React, { useMemo, useState } from 'react';
import { ILocalTableData } from '@/interfaces/indicator';
import Table from '@/components/molecules/table';
import './styles.scss';
import Skeleton from '@/components/atoms/skeletons';
import { provinceOrder } from '@/statics/static';
import Link from 'next/link';
import { useData } from '@/stores/context';
import { Collapse } from 'antd';
import Empty from '@/components/atoms/empty';
import { useDispatch, useSelector } from 'react-redux';
import { setDetailUrl } from '@/stores/modules/detailUrl';

interface TableProps {
  tableData: ILocalTableData | undefined;
  loading?: boolean;
}

const IndicatorTable: React.FC<TableProps> = ({ loading, tableData }) => {
  const { innerWidth } = useData();
  const dispatch = useDispatch();
  const userData = useSelector((state: any) => state.userData);
  const columns = useMemo(() => {
    const tagsDetails = tableData?.tagsDetail || [];
    if (!tagsDetails?.length) return [];

    const allChildren = tagsDetails?.flatMap(
      (tagDetail) => tagDetail?.localIndicator?.children || [],
    );

    const combinedChildren = Object.values(
      allChildren?.reduce(
        (acc, child) => {
          acc[child.name.trim()] = child;
          return acc;
        },
        {} as Record<string, (typeof allChildren)[0]>,
      ),
    );

    const filteredChildren = combinedChildren.filter((child) => {
      return provinceOrder.includes(child.name.trim());
    });

    const sortedChildren = [...filteredChildren].sort((a, b) => {
      const indexA = provinceOrder.indexOf(a.name);
      const indexB = provinceOrder.indexOf(b.name);
      return indexA - indexB;
    });

    return [
      {
        title: 'Chỉ số',
        dataIndex: 'indicatorName',
        key: 'indicatorName',
        fixed: 'left',
        width: 400,
      },
      {
        title: 'Thời điểm',
        dataIndex: 'time',
        key: 'time',
        fixed: 'left',
        width: 120,
      },
      ...sortedChildren.map((child) => ({
        title: child?.name,
        dataIndex: child?.name,
        key: child?.name,
        width: 160,
      })),
    ];
  }, [tableData]);

  const dataSource = useMemo(() => {
    const tagsDetails = tableData?.tagsDetail || [];

    if (!tagsDetails?.length) return [];

    const rows: Record<string, any> = {};

    tagsDetails.forEach((tagDetail, index) => {
      const children = tagDetail?.localIndicator?.children || [];
      const dataType = tagDetail?.localIndicator?.dataType || 'year';

      let maxTimeKey = -Infinity;
      children.forEach((child: any) => {
        const timeKey = child?.values?.[dataType];
        if (timeKey) {
          maxTimeKey = Math.max(maxTimeKey, Number(timeKey));
        }
      });

      children.forEach((child: any) => {
        const timeKey = child?.values?.[dataType];
        if (!timeKey || Number(timeKey) !== maxTimeKey) return;
        const rowKey =
          dataType === 'year'
            ? `${timeKey}-${tagDetail?.id}-${index}`
            : `${tagDetail?.id}-${index}`;
        if (!rows[rowKey]) {
          rows[rowKey] = {
            key: rowKey,
            time: timeKey,
            indicatorName: (
              <Link
                href={`/thong-tin-chi-tiet/${tagDetail?.localIndicator?.slug}/${tagDetail?.localIndicator?.id}?type=local_indicator&structure=${tagDetail?.localIndicator?.isStructure}&isObjectOneTime=${tagDetail?.localIndicator?.isObjectOneTime}`}
              >
                {tagDetail?.localIndicator?.name || '–'}
              </Link>
            ),
          };
        }
        rows[rowKey][child.name] =
          Number(child?.values?.value).toLocaleString('vi-VN') || '–';
      });
    });

    return Object.values(rows);
  }, [tableData]);

  const items = useMemo(() => {
    if (!tableData?.tagsDetail?.length) return [];
    const sortedTagsDetail = [...tableData.tagsDetail].sort((a, b) => {
      const nameA = a?.localIndicator?.name.trim() || '';
      const nameB = b?.localIndicator?.name.trim() || '';
      const indexA = provinceOrder.indexOf(nameA);
      const indexB = provinceOrder.indexOf(nameB);

      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    return sortedTagsDetail.map((tagDetail, index) => {
      const childrenList = tagDetail?.localIndicator?.children?.map((child) => {
        const timeKey =
          child?.values?.year ||
          child?.values?.quarter ||
          child?.values?.month ||
          child?.values?.day ||
          '–';
        const value = child?.values?.value
          ? Number(child?.values?.value).toLocaleString('vi-VN')
          : '–';

        return (
          <div key={child.id} className="t-table-collapse_row">
            <div className="t-table-collapse_row_col">{child.name}</div>
            <div className="t-table-collapse_row_col">{timeKey}</div>
            <div className="t-table-collapse_row_col">{value}</div>
          </div>
        );
      });

      return {
        key: index,
        label:
          (
            <>
              {tagDetail?.localIndicator?.name}{' '}
              <Link
                href={`/thong-tin-chi-tiet/${tagDetail?.localIndicator?.slug}/${tagDetail?.localIndicator?.id}?type=local_indicator&structure=${tagDetail?.localIndicator?.isStructure}&isObjectOneTime=${tagDetail?.localIndicator?.isObjectOneTime}`}
                onClick={() => {
                  dispatch(
                    setDetailUrl(
                      `/thong-tin-chi-tiet/${tagDetail?.localIndicator?.slug}/${tagDetail?.localIndicator?.id}?type=local_indicator&structure=${tagDetail?.localIndicator?.isStructure}&isObjectOneTime=${tagDetail?.localIndicator?.isObjectOneTime}`,
                    ),
                  );
                }}
              >
                [Chi tiết]
              </Link>
            </>
          ) || '–',
        children: (
          <>
            <div className="t-table-collapse_row">
              <div className="t-table-collapse_row_col">Tỉnh</div>
              <div className="t-table-collapse_row_col">Thời điểm</div>
              <div className="t-table-collapse_row_col">Giá trị</div>
            </div>
            {childrenList?.length ? childrenList : <Empty />}
          </>
        ),
      };
    });
  }, [tableData]);

  return (
    <div className="t-indicator-table">
      {loading || (
        <>
          <div className="t-indicator-table_title">
            Cập nhật kinh tế địa phương
          </div>
          <div className="line"></div>
        </>
      )}
      {loading ? (
        <Skeleton active type="default" loading paragraph={{ rows: 3 }} />
      ) : innerWidth > 1000 ? (
        <Table
          columns={columns}
          dataSource={dataSource}
          bordered
          scroll={{ x: 'max-content', y: 800 }}
          pagination={false}
          loading={loading}
        />
      ) : (
        <Collapse className="t-table-collapse" items={items} accordion={true} />
      )}
    </div>
  );
};

export default IndicatorTable;
